import { forgetDestination } from '@/auth/destination';
import { resetAnalytics } from '@/marketing/analytics';
import { forgetDrafts } from '@/pages/ansatt/draft';
import { forgetSaker } from '@/pages/vaktplan/saker';
import { endGeneration } from '@/shell/context';
import { create } from 'zustand';

/**
 * The token lives in a cookie named `token`, as the old app keeps it, so the
 * two frontends share a login on the same host during the parallel period.
 * httpOnly comes when the old app retires (plan 0019, Auth storage).
 *
 * A session is a token AND the person it was issued to (TF-WEB-COMPLETION C).
 * The person is stored next to the tail of that token; a token with no person,
 * a malformed person, or a person stored for a different token is not a
 * session: the shell never renders on it and no role is guessed. The old app
 * writes the cookie and never the person, so this is the state a person who
 * logged in over there arrives in.
 */
const COOKIE = 'token';
const USER_KEY = 'timefly.user';

export interface SessionUser {
  id: number;
  name: string;
  lastname?: string;
  email?: string;
  /** null for the narrow identity session, a person with no company yet (TF-ACCOUNT-ACCESS) */
  role: 'admin' | 'user' | string | null;
  tenant_id?: number;
  /** a support session (#23): Timefly staff see the app as this person, read only, until expires_at */
  support?: { session: number; company: string; expires_at: string };
}

/**
 * The staff member's own session while they look as somebody else (#23), in this tab only: closing the
 * tab forgets it, so a forgotten support session ends in a login, never in a second lasting credential.
 */
export const SUPPORT_RETURN = 'timefly.support.return';
export function supportReturn(): { token: string; user: SessionUser } | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(SUPPORT_RETURN) || 'null');
    return v && typeof v.token === 'string' && isUser(v.user) ? v : null;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}
function writeCookie(name: string, value: string | null) {
  if (value == null) document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
  else
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${60 * 60 * 12}; SameSite=Lax`;
}
/** Binds the stored person to the token without keeping a second copy of the credential. */
const tail = (token: string) => token.slice(-32);

function isUser(v: unknown): v is SessionUser {
  if (!v || typeof v !== 'object') return false;
  const u = v as Record<string, unknown>;
  return (
    typeof u.id === 'number' &&
    Number.isFinite(u.id) &&
    typeof u.name === 'string' &&
    // a string role, or null for the narrow identity session of a person with no company (TF-ACCOUNT-ACCESS)
    ((typeof u.role === 'string' && u.role.length > 0) || u.role === null)
  );
}
function readStoredUser(): string | null {
  try {
    return localStorage.getItem(USER_KEY);
  } catch {
    return null;
  }
}
/** The stored person, only when well formed and stored for this token. */
function parseUser(raw: string, token: string): SessionUser | null {
  try {
    const p = JSON.parse(raw) as { user?: unknown; tok?: unknown } | null;
    return p && isUser(p.user) && p.tok === tail(token) ? p.user : null;
  } catch {
    return null;
  }
}
function forgetUser() {
  try {
    localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

interface AuthState {
  token: string | null;
  user: SessionUser | null;
  /** the previous session ended without the person logging out: the login page says so */
  interrupted: boolean;
  setSession: (token: string, user: SessionUser) => void;
  clear: () => void;
}

/**
 * What this tab starts from. A token without a usable person is not adopted:
 * the person is dropped (it is local and invalid), the shared cookie is left
 * alone for the old app on the same host, and the login page explains. A
 * person without a token (the cookie expired) is dropped the same way.
 */
export function readSession(): Pick<AuthState, 'token' | 'user' | 'interrupted'> {
  const token = readCookie(COOKIE);
  const raw = readStoredUser();
  const user = token && raw ? parseUser(raw, token) : null;
  if (token && user) return { token, user, interrupted: false };
  forgetUser();
  return { token: null, user: null, interrupted: token != null || raw != null };
}

export const useAuth = create<AuthState>((set) => ({
  ...readSession(),
  setSession: (token, user) => {
    writeCookie(COOKIE, token);
    try {
      localStorage.setItem(USER_KEY, JSON.stringify({ user, tok: tail(token) }));
    } catch {
      /* private mode */
    }
    set({ token, user, interrupted: false });
  },
  clear: () => {
    resetAnalytics(); // the next person on this browser is someone new (issue #15)
    writeCookie(COOKIE, null);
    forgetUser();
    forgetDestination(); // the site's ?next= was this person's; the next one on this browser starts fresh
    forgetDrafts(); // unsaved employee forms were this person's (#270)
    forgetSaker(); // Saker's deferrals, finished tasks and drafts were this person's (#286)
    set({ token: null, user: null, interrupted: false });
    endGeneration();
  },
}));

export const getToken = (): string | null => useAuth.getState().token;
/** The token in the shared cookie right now. Differs from the state's when another tab changed the session. */
export const cookieToken = (): string | null => readCookie(COOKIE);
/** The request layer's 401: the server refused the session, so the login page says to log in again. */
export const logout = (): void => {
  // a support session that ended or expired goes back to the staff member's own session (#23)
  const back = useAuth.getState().user?.support ? supportReturn() : null;
  if (back) {
    try {
      sessionStorage.removeItem(SUPPORT_RETURN);
    } catch {
      /* ignore */
    }
    useAuth.getState().setSession(back.token, back.user);
    endGeneration();
    return;
  }
  useAuth.getState().clear();
  useAuth.setState({ interrupted: true });
};
